/**
 * Raccourcis clavier pour le jeu
 *
 * Flèches → déplacer le pion dans cette direction
 * W       → activer/désactiver le mode mur
 * R       → rotation de l'orientation du mur
 * Escape  → quitter le mode mur
 */

import { useEffect } from 'react';
import { useGameStore } from '../store/gameStore';
import { socketService } from '../services/socketService';
import { getValidMoves } from '@pathblock/core';

export function useKeyboard(): void {
  const store = useGameStore;

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      // Ne pas réagir si focus sur un input
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      const state = store.getState();
      const { gameState, playerId, view } = state;

      if (view !== 'playing' || !gameState || !playerId) return;
      if (gameState.phase !== 'playing') return;
      if (gameState.currentPlayer !== playerId) return;

      // Escape → quitter mode mur
      if (e.key === 'Escape') {
        e.preventDefault();
        state.setPlacingWall(false);
        return;
      }

      // W → toggle mode mur
      if (e.key === 'w' || e.key === 'W') {
        e.preventDefault();
        const myPlayer = gameState.players[playerId - 1];
        if (myPlayer.wallsRemaining > 0) {
          state.setPlacingWall(!state.placingWall);
        }
        return;
      }

      // R → rotation du mur
      if (e.key === 'r' || e.key === 'R') {
        e.preventDefault();
        state.toggleWallOrientation();
        return;
      }

      // Flèches → déplacement (seulement si pas en mode mur)
      if (state.placingWall) return;

      const dirMap: Record<string, { dr: number; dc: number }> = {
        ArrowUp:    { dr: -1, dc: 0 },
        ArrowDown:  { dr: 1,  dc: 0 },
        ArrowLeft:  { dr: 0,  dc: -1 },
        ArrowRight: { dr: 0,  dc: 1 },
      };

      const dir = dirMap[e.key];
      if (!dir) return;

      e.preventDefault();

      const myPos = gameState.players[playerId - 1].position;
      const validMoves = getValidMoves(gameState, playerId);

      // Si joueur 1 (vue inversée), inverser les directions
      const flipDir = playerId === 1;
      const actualDr = flipDir ? -dir.dr : dir.dr;
      const actualDc = flipDir ? -dir.dc : dir.dc;

      const target = {
        row: myPos.row + actualDr,
        col: myPos.col + actualDc,
      };

      // Vérifier si ce mouvement est valide (peut inclure sauts)
      // Chercher le mouvement valide dans la direction demandée
      const match = validMoves.find((m) => {
        const dr = m.row - myPos.row;
        const dc = m.col - myPos.col;
        // Direction correspondante (normaliser)
        return Math.sign(dr) === Math.sign(actualDr) && Math.sign(dc) === Math.sign(actualDc)
          && (actualDr === 0 || dc === 0)
          && (actualDc === 0 || dr === 0);
      });

      const moveTarget = match ?? (
        validMoves.find(m => m.row === target.row && m.col === target.col)
      );

      if (moveTarget) {
        socketService.move(moveTarget);
      }
    };

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [store]);
}
